import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, Coins, Lock, Info } from 'lucide-react';
import { API } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { ShareButton } from '../components/ShareComponents';

const COIN_YANG = 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/zwtqm110_coin1.png';
const COIN_YIN = 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/awi6e6p6_coin2.png';

const LINE_LABELS = {
  6: { type: 'yin', changing: true, label: 'Yin Viejo', symbol: '⚋', arrow: '→ Yang' },
  7: { type: 'yang', changing: false, label: 'Yang Joven', symbol: '⚊', arrow: '' },
  8: { type: 'yin', changing: false, label: 'Yin Joven', symbol: '⚋', arrow: '' },
  9: { type: 'yang', changing: true, label: 'Yang Viejo', symbol: '⚊', arrow: '→ Yin' },
};

function CoinTossAnimation({ onComplete }) {
  const [tossIndex, setTossIndex] = useState(0);
  const [coins, setCoins] = useState([null, null, null]);
  const [lineResults, setLineResults] = useState([]);
  const [tossing, setTossing] = useState(false);

  const tossSingle = useCallback(() => {
    if (tossIndex >= 6) return;
    setTossing(true);

    // Animate coins for 1.2s then show result
    setTimeout(() => {
      const results = [0, 1, 2].map(() => (Math.random() > 0.5 ? 3 : 2));
      setCoins(results);
      const total = results.reduce((a, b) => a + b, 0);
      setLineResults(prev => [...prev, total]);
      setTossing(false);
      setTossIndex(prev => prev + 1);
    }, 1200);
  }, [tossIndex]);

  useEffect(() => {
    if (tossIndex >= 6 && lineResults.length === 6) {
      setTimeout(() => onComplete(lineResults), 600);
    }
  }, [tossIndex, lineResults, onComplete]);

  return (
    <div className="text-center">
      <p className="text-[var(--text-muted)] mb-2 text-sm">
        Lanzamiento {Math.min(tossIndex + 1, 6)} de 6
      </p>
      <div className="w-full bg-white/5 rounded-full h-1.5 mb-6">
        <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${(tossIndex / 6) * 100}%`, background: 'var(--accent-gold)' }} />
      </div>

      {/* Coins display */}
      <div className="flex justify-center gap-6 sm:gap-8 mb-8">
        {coins.map((coin, i) => (
          <div key={`${tossIndex}-${i}`} className="flex flex-col items-center">
            <motion.div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center overflow-hidden"
              style={{
                background: coin === null && !tossing ? 'rgba(255,255,255,0.05)' : 'transparent',
                border: coin === null && !tossing ? '2px solid rgba(255,255,255,0.1)' : 'none',
              }}
              animate={tossing ? { rotateY: [0, 180, 360, 540, 720], scale: [1, 1.15, 1, 1.15, 1] } : {}}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
            >
              {tossing ? (
                <img
                  src={i % 2 === 0 ? COIN_YANG : COIN_YIN}
                  alt="moneda"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-lg"
                />
              ) : coin !== null ? (
                <img
                  src={coin === 3 ? COIN_YANG : COIN_YIN}
                  alt={coin === 3 ? 'Yang' : 'Yin'}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-lg"
                />
              ) : (
                <span className="text-xl text-[var(--text-muted)]">?</span>
              )}
            </motion.div>
            {coin !== null && !tossing && (
              <motion.span
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className={`text-xs font-bold mt-2 ${coin === 3 ? 'text-gold-300' : 'text-purple-400'}`}
              >
                {coin === 3 ? 'Yang ⚊' : 'Yin ⚋'}
              </motion.span>
            )}
          </div>
        ))}
      </div>

      {/* Lines built so far */}
      <div className="flex flex-col-reverse gap-2 items-center mb-8 min-h-[160px]">
        {lineResults.map((line, i) => {
          const info = LINE_LABELS[line];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <span className="text-xs text-[var(--text-muted)] w-6 text-right">{i + 1}</span>
              <div className="flex items-center gap-1">
                {info.type === 'yang' ? (
                  <div className="w-32 h-3 rounded-sm" style={{ background: info.changing ? 'var(--accent-gold)' : 'var(--text-primary)' }} />
                ) : (
                  <div className="flex gap-2">
                    <div className="w-14 h-3 rounded-sm" style={{ background: info.changing ? 'var(--accent-gold)' : 'var(--text-primary)' }} />
                    <div className="w-14 h-3 rounded-sm" style={{ background: info.changing ? 'var(--accent-gold)' : 'var(--text-primary)' }} />
                  </div>
                )}
              </div>
              {info.changing && <span className="text-xs text-gold-300 font-medium">{info.arrow}</span>}
            </motion.div>
          );
        })}
      </div>

      {tossIndex < 6 && (
        <button
          onClick={tossSingle}
          disabled={tossing}
          className="btn-gold flex items-center gap-2 mx-auto"
          data-testid="toss-coins-btn"
        >
          {tossing ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Lanzando...</>
          ) : (
            <><Coins className="w-5 h-5" /> Lanzar Monedas</>
          )}
        </button>
      )}
      {tossIndex >= 6 && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold-300 font-heading">
          Consultando el oraculo...
        </motion.p>
      )}
    </div>
  );
}

function HexagramDisplay({ data }) {
  if (!data) return null;
  const { primary, lines, changing_lines, has_mutations, mutated, lower_trigram, upper_trigram } = data;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Primary hexagram */}
      <div className="glass-card p-6 text-center" data-testid="primary-hexagram">
        <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-2">Hexagrama Principal</p>
        <p className="text-6xl mb-2">{primary.character}</p>
        <p className="text-lg font-bold gold-text font-heading">#{primary.number} - {primary.name_es}</p>
        <p className="text-sm text-purple-400 italic mb-4">{primary.name}</p>

        {/* Lines visual */}
        <div className="flex flex-col-reverse gap-1.5 items-center my-4">
          {lines.map((line, i) => {
            const info = LINE_LABELS[line];
            const isChanging = changing_lines.includes(i + 1);
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-muted)] w-4 text-right">{i + 1}</span>
                {info.type === 'yang' ? (
                  <div className="w-20 h-2.5 rounded-sm" style={{ background: isChanging ? 'var(--accent-gold)' : 'var(--text-secondary)' }} />
                ) : (
                  <div className="flex gap-1.5">
                    <div className="w-9 h-2.5 rounded-sm" style={{ background: isChanging ? 'var(--accent-gold)' : 'var(--text-secondary)' }} />
                    <div className="w-9 h-2.5 rounded-sm" style={{ background: isChanging ? 'var(--accent-gold)' : 'var(--text-secondary)' }} />
                  </div>
                )}
                {isChanging && <span className="text-[10px] text-gold-300">*</span>}
              </div>
            );
          })}
        </div>

        {/* Trigrams */}
        <div className="flex justify-center gap-4 text-sm mt-4">
          <div className="bg-black/30 rounded-lg px-3 py-2">
            <span className="text-xl">{upper_trigram?.symbol}</span>
            <p className="text-[10px] text-[var(--text-muted)]">{upper_trigram?.name_es}</p>
          </div>
          <div className="bg-black/30 rounded-lg px-3 py-2">
            <span className="text-xl">{lower_trigram?.symbol}</span>
            <p className="text-[10px] text-[var(--text-muted)]">{lower_trigram?.name_es}</p>
          </div>
        </div>
      </div>

      {/* Mutated hexagram */}
      {has_mutations && mutated ? (
        <div className="glass-card p-6 text-center" data-testid="mutated-hexagram">
          <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-2">Hexagrama Mutante</p>
          <p className="text-6xl mb-2">{mutated.character}</p>
          <p className="text-lg font-bold gold-text font-heading">#{mutated.number} - {mutated.name_es}</p>
          <p className="text-sm text-purple-400 italic mb-4">{mutated.name}</p>

          <div className="bg-black/30 rounded-lg p-4 text-left mt-4">
            <p className="text-xs text-[var(--text-muted)] mb-1">Juicio:</p>
            <p className="text-sm text-[var(--text-secondary)] italic">{mutated.judgment}</p>
          </div>
          <p className="text-xs text-gold-300/70 mt-4">
            Lineas cambiantes: {changing_lines.join(', ')}
          </p>
        </div>
      ) : (
        <div className="glass-card p-6 text-center flex flex-col items-center justify-center">
          <Info className="w-8 h-8 text-[var(--text-muted)] mb-3" />
          <p className="text-sm text-[var(--text-muted)]">Sin lineas cambiantes</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">El hexagrama se mantiene estable. La situacion es clara y definida.</p>
        </div>
      )}
    </div>
  );
}

export default function IchingPage() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState('select'); // select | question | tossing | result
  const [readingType, setReadingType] = useState('simple');
  const [question, setQuestion] = useState('');
  const [freeLimit, setFreeLimit] = useState(null);
  const [readingTypes, setReadingTypes] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/iching/free-limit`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ can_read_free: true })),
      fetch(`${API}/iching/reading-types`).then(r => r.json()).catch(() => null),
    ]).then(([limit, types]) => {
      setFreeLimit(limit);
      setReadingTypes(types);
    });
  }, []);

  const startReading = async () => {
    // Refresh free limit before proceeding
    if (readingType === 'simple') {
      try {
        const limitRes = await fetch(`${API}/iching/free-limit`, { credentials: 'include' });
        const limit = await limitRes.json();
        setFreeLimit(limit);
        if (!limit.can_read_free) {
          toast.error('Ya usaste tu lectura gratuita de hoy. Vuelve manana o prueba una lectura con creditos.');
          return;
        }
      } catch (e) { /* continue anyway */ }
    }
    const rtype = readingTypes?.[readingType];
    if (rtype?.credits > 0 && (!user || user.credits < rtype.credits)) {
      toast.error(`Necesitas ${rtype.credits} creditos para esta lectura.`);
      return;
    }
    setStep('question');
  };

  const startTossing = () => {
    setStep('tossing');
  };

  const onTossComplete = async (lines) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/iching/cast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reading_type: readingType, question, lines }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setStep('result');
        // Refresh user data (credits, stats) after successful reading
        if (refreshUser) refreshUser();
        // Refresh free limit status
        fetch(`${API}/iching/free-limit`, { credentials: 'include' }).then(r => r.json()).then(setFreeLimit).catch(() => {});
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || 'Error al consultar el oraculo. Intenta de nuevo.';
        toast.error(msg, { duration: 5000 });
        // Refresh free limit in case it was consumed
        fetch(`${API}/iching/free-limit`, { credentials: 'include' }).then(r => r.json()).then(setFreeLimit).catch(() => {});
        setStep('select');
      }
    } catch (e) {
      toast.error('Error de conexion. Verifica tu internet e intenta de nuevo.', { duration: 5000 });
      setStep('select');
    }
    setLoading(false);
  };

  const resetReading = () => {
    setStep('select');
    setResult(null);
    setQuestion('');
    // Refresh free limit
    fetch(`${API}/iching/free-limit`, { credentials: 'include' }).then(r => r.json()).then(setFreeLimit).catch(() => {});
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl sm:text-4xl font-bold gold-text text-center mb-2 font-heading" data-testid="iching-title">I Ching</h1>
          <p className="text-[var(--text-muted)] text-center mb-8 text-sm">El Libro de los Cambios - Sabiduria milenaria china</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Select reading type */}
          {step === 'select' && (
            <motion.div key="select" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {readingTypes && Object.entries(readingTypes).map(([key, rtype]) => {
                  const isFree = rtype.credits === 0;
                  const canAfford = isFree || (user && user.credits >= rtype.credits);
                  const isFreeLimitReached = isFree && freeLimit && !freeLimit.can_read_free;
                  return (
                    <div
                      key={key}
                      onClick={() => setReadingType(key)}
                      className={`glass-card p-6 cursor-pointer transition-all ${readingType === key ? 'border-gold-300/40 ring-1 ring-gold-300/20' : ''} ${isFreeLimitReached && isFree ? 'opacity-60' : ''}`}
                      data-testid={`reading-type-${key}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold font-heading text-sm">{rtype.name}</h3>
                        {isFree ? (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">GRATIS</span>
                        ) : (
                          <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Coins className="w-3 h-3" /> {rtype.credits}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-muted)]">{rtype.description}</p>
                      {isFreeLimitReached && isFree && (
                        <p className="text-xs text-orange-400 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Usada hoy</p>
                      )}
                      {!canAfford && !isFree && (
                        <p className="text-xs text-red-400 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Creditos insuficientes</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <button onClick={startReading} className="btn-gold" data-testid="start-iching-btn">
                  Comenzar Consulta
                </button>
              </div>

              {/* Info section */}
              <div className="mt-12 glass-card p-8">
                <h2 className="text-xl font-bold gold-text text-center mb-6 font-heading">Que es el I Ching?</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { char: '☰', title: 'Oraculo Milenario', desc: 'El I Ching o Libro de los Cambios es un antiguo texto chino de mas de 3.000 anos de sabiduria.' },
                    { char: '☯', title: 'Yin y Yang', desc: 'Basado en la dualidad de fuerzas opuestas y complementarias que gobiernan el universo.' },
                    { char: '卦', title: '64 Hexagramas', desc: 'Cada hexagrama es una combinacion de 6 lineas que revela un aspecto de la vida y el cambio.' },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <span className="text-4xl block mb-3">{item.char}</span>
                      <h3 className="font-bold font-heading text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-[var(--text-muted)]">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Question */}
          {step === 'question' && (
            <motion.div key="question" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="glass-card p-8 max-w-xl mx-auto">
              <h2 className="text-xl font-bold gold-text text-center mb-2 font-heading">Formula tu Pregunta</h2>
              <p className="text-[var(--text-muted)] text-center text-sm mb-6">
                Concentra tu mente en una pregunta o situacion. Puedes dejarla en blanco para una consulta general.
              </p>
              <textarea
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Escribe tu pregunta aqui (opcional)..."
                className="mystic-input w-full h-28 resize-none mb-6"
                data-testid="iching-question-input"
              />
              <div className="flex gap-3 justify-center">
                <button onClick={() => setStep('select')} className="btn-ghost">Volver</button>
                <button onClick={startTossing} className="btn-gold" data-testid="proceed-toss-btn">
                  <Coins className="w-5 h-5 inline mr-2" /> Lanzar las Monedas
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Coin toss animation */}
          {step === 'tossing' && !loading && (
            <motion.div key="tossing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="glass-card p-8 max-w-xl mx-auto">
              <h2 className="text-xl font-bold gold-text text-center mb-6 font-heading">Lanzamiento de Monedas</h2>
              <CoinTossAnimation onComplete={onTossComplete} />
            </motion.div>
          )}

          {/* Loading state */}
          {loading && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-gold-300 mb-4" />
              <p className="text-gold-300 font-heading">Interpretando el oraculo...</p>
              <p className="text-[var(--text-muted)] text-sm mt-2">El sabio consulta el Libro de los Cambios</p>
            </motion.div>
          )}

          {/* STEP 4: Result */}
          {step === 'result' && result && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {/* Reading type badge */}
              <div className="text-center mb-6">
                <span className="inline-block bg-purple-500/20 text-purple-300 px-4 py-1 rounded-full text-sm">
                  {result.reading_type_name}
                </span>
                {result.question && (
                  <p className="text-[var(--text-muted)] text-sm mt-2 italic">"{result.question}"</p>
                )}
              </div>

              {/* Hexagram display */}
              <HexagramDisplay data={result.hexagram_data} />

              {/* Interpretation */}
              <div className="glass-card p-8 mt-6" data-testid="iching-interpretation">
                <h3 className="text-lg font-bold text-purple-300 mb-4 font-heading">Interpretacion del Oraculo</h3>
                <MarkdownRenderer content={result.interpretation} className="text-[var(--text-secondary)]" />
              </div>

              {/* Credits & Share */}
              <div className="flex items-center justify-between mt-6">
                {result.credits_used > 0 && (
                  <p className="text-sm text-[var(--text-muted)]">Creditos utilizados: {result.credits_used}</p>
                )}
                <div className="flex items-center gap-3 ml-auto">
                  <ShareButton
                    type="iching"
                    content={{
                      hexagram_number: result.hexagram_data?.primary?.number,
                      hexagram_name: result.hexagram_data?.primary?.name_es,
                      hexagram_character: result.hexagram_data?.primary?.character,
                      reading_type: result.reading_type_name,
                      question: result.question,
                      interpretation: result.interpretation?.substring(0, 300),
                      has_mutations: result.hexagram_data?.has_mutations,
                      mutated_name: result.hexagram_data?.mutated?.name_es,
                      mutated_character: result.hexagram_data?.mutated?.character,
                    }}
                    title={`I Ching: ${result.hexagram_data?.primary?.name_es} (${result.hexagram_data?.primary?.character})`}
                  />
                  <button onClick={resetReading} className="btn-ghost" data-testid="new-reading-btn">
                    Nueva Consulta
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
